// Phase 2 retention systems: Power Cells, Supply Drops, Daily Supply, Directives, Milestones,
// Salvage Shop boosts. Headless like the rest of the core: time comes from game.now().
import { BALANCE } from '../data/balance';
import { D, Decimal, ZERO } from '../core/decimal';
import { mulberry32 } from '../core/rng';
import { PETS } from '../data/pets';
import {
  DAILY_SUPPLY, DIRECTIVES_BONUS, DIRECTIVES_PER_DAY, DIRECTIVE_POOL, MILESTONES, MILESTONE_TUNING,
  SHOP_ITEMS, SUPPLY_DROP, type BoostId, type DirectiveMetric, type MilestoneDef, type Reward,
} from '../data/meta';
import type { Rarity } from '../data/equipment';
import { rollItem, type Item } from './equipment';
import type { Game } from './game';
import type { ModifierSource } from './stats';

// ---- Persistent state -----------------------------------------------------------------------

export interface DirectiveState { id: string; progress: number; claimed: boolean }

export interface MetaState {
  supplyNextAt: number;                 // epoch ms of the next Supply Drop (0 = schedule on load)
  daily: { streak: number; lastDay: string | null };
  directives: { day: string | null; list: DirectiveState[]; bonusClaimed: boolean };
  milestones: Record<string, number>;   // milestone id -> tiers claimed
  boosts: Partial<Record<BoostId, number>>; // boost -> active until (epoch ms)
}

export function emptyMeta(): MetaState {
  return {
    supplyNextAt: 0,
    daily: { streak: 0, lastDay: null },
    directives: { day: null, list: [], bonusClaimed: false },
    milestones: {},
    boosts: {},
  };
}

// ---- Dates ----------------------------------------------------------------------------------

/** Local calendar day, e.g. "2026-09-24". Daily resets happen at local midnight. */
export function dayKey(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function previousDay(key: string): string {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number];
  return dayKey(new Date(y, m - 1, d - 1, 12).getTime());
}

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

// ---- Rewards --------------------------------------------------------------------------------

export interface RewardResult {
  cells: number;
  scrap: Decimal;
  parts: { petId: string; amount: number } | null;
  energy: boolean;
  item: Item | null;
}

export function describeReward(r: Reward): string {
  const parts: string[] = [];
  if (r.cells) parts.push(`💎 ${r.cells}`);
  if (r.scrapKills) parts.push(`🪙 Coins (${r.scrapKills} enemies' worth)`);
  if (r.parts) parts.push(`🍪 ${r.parts} treats`);
  if (r.energy) parts.push('🗝️ energy');
  if (r.item) parts.push(`${r.item[0]!.toUpperCase()}${r.item.slice(1)}+ gear`);
  return parts.join(' · ');
}

// ---- The systems ----------------------------------------------------------------------------

export class Meta {
  constructor(private game: Game) {
    const e = game.events;
    e.on('kill', (k) => { this.progress('kills', 1); if (k.monster.boss) this.progress('bossKills', 1); });
    e.on('damage', (d) => { if (d.source === 'tap') this.progress('taps', 1); });
    e.on('skillUsed', () => { this.game.state.lifetime.casts++; this.progress('casts', 1); });
    e.on('purchase', (p) => { if (p.kind === 'hero') this.progress('botLevels', p.count); });
    e.on('stageCleared', () => this.progress('stages', 1));
    e.on('prestige', () => this.progress('reboots', 1));
    e.on('petLeveled', () => this.progress('partsFed', 1));
  }

  private get s() { return this.game.state; }

  // ---------- rewards ----------

  grant(r: Reward, source: string): RewardResult {
    const g = this.game, st = this.s;
    const out: RewardResult = { cells: 0, scrap: ZERO, parts: null, energy: false, item: null };
    if (r.cells) { st.cells += r.cells; out.cells = r.cells; }
    if (r.scrapKills) {
      out.scrap = g.goldAt(st.stage).mul(g.stats.mult('gold')).mul(r.scrapKills).floor();
      st.gold = st.gold.add(out.scrap);
    }
    if (r.parts) {
      const pet = PETS[Math.floor(g.random() * PETS.length)]!;
      st.pets.food[pet.id] = (st.pets.food[pet.id] ?? 0) + r.parts;
      out.parts = { petId: pet.id, amount: r.parts };
    }
    if (r.energy) {
      st.mana = r.energy === 'full' ? g.maxMana() : Math.min(g.maxMana(), st.mana + r.energy);
      out.energy = true;
    }
    if (r.item) out.item = this.grantItem(r.item);
    g.events.emit('rewardGranted', { source, reward: r, result: out });
    return out;
  }

  private grantItem(floor: Rarity): Item {
    const g = this.game;
    const item = rollItem(Math.max(1, this.s.bestStage), () => g.random(), this.s.equipment.nextUid++, floor);
    g.addItem(item);
    return item;
  }

  // ---------- boosts ----------

  boostActive(id: BoostId): boolean { return (this.s.meta.boosts[id] ?? 0) > this.game.now(); }
  boostLeftSec(id: BoostId): number { return Math.max(0, ((this.s.meta.boosts[id] ?? 0) - this.game.now()) / 1000); }

  // ---------- Supply Drops ----------

  /** Active drop, if one is on screen: when it appeared and when it leaves. */
  supplyDrop: { spawnedAt: number; expiresAt: number } | null = null;

  private scheduleDrop(): void {
    const { minGapSec, maxGapSec } = SUPPLY_DROP;
    this.s.meta.supplyNextAt = this.game.now() + (minGapSec + this.game.random() * (maxGapSec - minGapSec)) * 1000;
  }

  update(): void {
    const t = this.game.now();
    if (this.s.meta.supplyNextAt === 0) this.scheduleDrop();
    if (this.supplyDrop && t >= this.supplyDrop.expiresAt) {
      this.supplyDrop = null;
      this.scheduleDrop();
      this.game.events.emit('supplyDropMissed', {});
    } else if (!this.supplyDrop && t >= this.s.meta.supplyNextAt) {
      this.supplyDrop = { spawnedAt: t, expiresAt: t + SUPPLY_DROP.lifetimeSec * 1000 };
      this.game.events.emit('supplyDropSpawned', {});
    }
  }

  catchSupplyDrop(): RewardResult | null {
    if (!this.supplyDrop) return null;
    this.supplyDrop = null;
    this.scheduleDrop();
    const total = SUPPLY_DROP.table.reduce((n, e) => n + e.weight, 0);
    let roll = this.game.random() * total;
    const entry = SUPPLY_DROP.table.find((e) => (roll -= e.weight) <= 0) ?? SUPPLY_DROP.table[0]!;
    this.s.lifetime.drops++;
    this.progress('drops', 1);
    return this.grant(entry.reward, `supply:${entry.label}`);
  }

  // ---------- Daily Supply ----------

  today(): string { return dayKey(this.game.now()); }
  dailyClaimable(): boolean { return this.s.meta.daily.lastDay !== this.today(); }

  /** Streak day (1-7) that claiming now would give. */
  nextDailyDay(): number {
    const d = this.s.meta.daily;
    if (!this.dailyClaimable()) return d.streak;
    const continues = d.lastDay !== null && d.lastDay === previousDay(this.today());
    return continues ? (d.streak % DAILY_SUPPLY.length) + 1 : 1;
  }

  claimDaily(): RewardResult | null {
    if (!this.dailyClaimable()) return null;
    const day = this.nextDailyDay();
    this.s.meta.daily = { streak: day, lastDay: this.today() };
    return this.grant(DAILY_SUPPLY[day - 1]!, `daily:${day}`);
  }

  // ---------- Directives ----------

  /** Today's directives, rolled deterministically from the date so a reload never rerolls. */
  directives(): DirectiveState[] {
    const d = this.s.meta.directives, today = this.today();
    if (d.day !== today) {
      const rng = mulberry32(hash(today));
      const pool = [...DIRECTIVE_POOL];
      const list: DirectiveState[] = [];
      while (list.length < DIRECTIVES_PER_DAY && pool.length) {
        const pick = pool.splice(Math.floor(rng() * pool.length), 1)[0]!;
        // One directive per metric per day keeps the three varied.
        if (list.some((x) => DIRECTIVE_POOL.find((p) => p.id === x.id)!.metric === pick.metric)) continue;
        list.push({ id: pick.id, progress: 0, claimed: false });
      }
      this.s.meta.directives = { day: today, list, bonusClaimed: false };
    }
    return this.s.meta.directives.list;
  }

  private progress(metric: DirectiveMetric, amount: number): void {
    for (const d of this.directives()) {
      const def = DIRECTIVE_POOL.find((p) => p.id === d.id);
      if (def && def.metric === metric && !d.claimed) d.progress = Math.min(def.target, d.progress + amount);
    }
  }

  claimDirective(id: string): RewardResult | null {
    const d = this.directives().find((x) => x.id === id);
    const def = DIRECTIVE_POOL.find((p) => p.id === id);
    if (!d || !def || d.claimed || d.progress < def.target) return null;
    d.claimed = true;
    this.s.lifetime.directives++;
    return this.grant(def.reward, `directive:${id}`);
  }

  directivesBonusClaimable(): boolean {
    const list = this.directives();
    return list.length > 0 && list.every((d) => d.claimed) && !this.s.meta.directives.bonusClaimed;
  }

  claimDirectivesBonus(): RewardResult | null {
    if (!this.directivesBonusClaimable()) return null;
    this.s.meta.directives.bonusClaimed = true;
    return this.grant(DIRECTIVES_BONUS, 'directive:bonus');
  }

  // ---------- Milestones ----------

  milestoneValue(m: MilestoneDef): number {
    const st = this.s, g = this.game;
    switch (m.metric) {
      case 'bestStage': return st.bestStage;
      case 'kills': return st.lifetime.kills;
      case 'bossKills': return st.lifetime.bossKills;
      case 'taps': return st.lifetime.taps;
      case 'reboots': return st.ascensions;
      case 'botsOwned': return Object.keys(st.heroLevels).length;
      case 'techOwned': return g.artifactsOwned();
      case 'drops': return st.lifetime.drops;
      case 'directives': return st.lifetime.directives;
    }
  }

  milestoneClaimed(m: MilestoneDef): number { return this.s.meta.milestones[m.id] ?? 0; }

  milestoneClaimable(m: MilestoneDef): boolean {
    const tier = this.milestoneClaimed(m);
    return tier < m.tiers.length && this.milestoneValue(m) >= m.tiers[tier]!;
  }

  milestoneCells(tierIndex: number): number {
    return MILESTONE_TUNING.cellsBase + MILESTONE_TUNING.cellsPerTier * tierIndex;
  }

  claimMilestone(id: string): RewardResult | null {
    const m = MILESTONES.find((x) => x.id === id);
    if (!m || !this.milestoneClaimable(m)) return null;
    const tier = this.milestoneClaimed(m);
    this.s.meta.milestones[id] = tier + 1;
    this.game.invalidate(); // milestone damage bonus
    return this.grant({ cells: this.milestoneCells(tier) }, `milestone:${id}:${tier + 1}`);
  }

  tiersClaimed(): number {
    return Object.values(this.s.meta.milestones).reduce((a, b) => a + b, 0);
  }

  // ---------- Salvage Shop ----------

  buy(itemId: string): boolean {
    const item = SHOP_ITEMS.find((i) => i.id === itemId);
    if (!item || this.s.cells < item.cost) return false;
    this.s.cells -= item.cost;
    if (item.effect.kind === 'reward') {
      this.grant(item.effect.reward, `shop:${item.id}`);
      // Purchased Gems are spent first; with coinPower.paidOnly only their share feeds the Rewind bonus.
      const paid = Math.min(item.cost, this.s.paidCells);
      this.s.paidCells -= paid;
      const share = BALANCE.coinPower.paidOnly ? paid / item.cost : 1;
      this.s.runBoughtKills += (item.effect.reward.scrapKills ?? 0) * share;
    }
    else {
      const b = this.s.meta.boosts, t = this.game.now();
      b[item.effect.boost] = Math.max(t, b[item.effect.boost] ?? 0) + item.effect.minutes * 60_000;
      this.game.events.emit('boostStarted', { boost: item.effect.boost });
    }
    return true;
  }
}

/** Claimed milestone tiers each add a small, permanent, multiplicative all-damage bonus. */
export function milestoneSource(): ModifierSource {
  return {
    id: 'milestones',
    apply(state, m) {
      const tiers = Object.values(state.meta.milestones).reduce((a, b) => a + b, 0);
      if (tiers > 0) m.mult('allDamage', D(1 + MILESTONE_TUNING.damagePerTier).pow(tiers));
    },
  };
}
